import Button from "components/Button/Button";
import { useState } from "react";
import styles from "./CurrentRoute.module.scss";
import { ReactComponent as DraggableIcon } from "assets/draggable.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { store } from 'stores/Store';
import { Tab } from "Modules/MapRouter";
import React from "react";
import useExternalApiService from "helpers/ExternalApiService";
import SearchModal from "./components/SearchModal";
import { ReactComponent as HouseIcon } from "assets/house.svg";
import { ReactComponent as CloseIcon } from "assets/xCircle.svg";
import { ReactComponent as FlagIcon } from "assets/flag.svg";
import MeetingModal from "./components/MeetingModal";
import StartTimeModal from "./components/StartTimeModal";
import Toastr from "components/Toastr";
import { decode } from "@googlemaps/polyline-codec";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import RoutesTab from "Modules/PractitionerBaseballCard/components/RoutesTab";
import { Stack, Drawer, Box, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import { observer } from "mobx-react";
import RouteCalendarExport from "./components/RouteCalendarExport";

interface IMapRouterProps {
  setSelectedTab: (tab: Tab) => void;
  setSelectedView: (view: string) => void;
  currentRouteName: string;
  currentRoute: any;
  clearCurrentRoute: () => void;
  setHasRouteChanged: (hasRouteChanged: boolean) => void;
  setShowUnsavedChangesModal: (showUnsavedChangesModal: boolean) => void;
  hasRouteChanged: boolean;
  showUnsavedChangesModal: boolean;
  clickedClear: boolean;
  isDesktopMap: boolean;
  setClickedClear: (clickedClear: boolean) => void;
  onRouteChange: (v: number) => void;
  onRouteSave: (id?: number) => void;
  setRouteName: (routeName: string) => void;
  onSelectPractitioner: () => void;
}

interface Waypoint {
  travelTime?: number;
  waitDuration?: number;
  id?: number;
  address?: string;
  latitude?: string;
  longitude?: string;
  isFirstLocation?: boolean;
  isLastLocation?: boolean;
  start_time?: string;
  duration?: string;
  end_time?: string;
  arriveTime?: string;
  name?: string;
}

interface PlaceSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

interface PlacesResponse {
  success: boolean;
  error_msg: string;
  places: PlaceSuggestion[];
}
// Module-scoped so it survives component remounts (tab switches, drawer
// close/reopen) — a per-mount ref made every remount re-fire a billable
// Google Route Optimization call for waypoints that were already computed.
let lastOptimizedWaypointsKey = "";
// Encoded polyline of the last computed route; module-scoped for the same
// reason, so a remount between computing and saving doesn't drop it.
let lastRoutePolyline = "";

// Keyed on route id as well as the stops, so two distinct saved routes that
// happen to share the same stop sequence don't collide (which would make the
// second one skip its recompute and render without ETAs).
const waypointsFingerprint = (routeId: number | string | undefined, wps: Array<Waypoint | null>) =>
  (routeId ?? 'new') + '::' + JSON.stringify(wps.filter(wp => wp !== null).map(w => ({ id: w?.id, lat: w?.latitude, lng: w?.longitude })));

const parseStartTime = (startTime: string) => {
  if (!startTime) return "";
  if (startTime.includes("AM") || startTime.includes("PM")) {
    const [time, period] = startTime.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    const isPM = period === 'PM';
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
      
    return `${year}-${month}-${day} ${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  }
  const date = new Date(startTime);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
}
const formatTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to convert datetime to API format (ISO format)
const convertToApiFormat = (dateTimeStr: string) => {
  if (!dateTimeStr) return "";
  
  // If already in ISO format, return as is
  if (dateTimeStr.includes('T') && dateTimeStr.includes('Z')) {
    return dateTimeStr;
  }
  
  // If space-separated format, convert to ISO
  if (dateTimeStr.includes(' ') && dateTimeStr.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
    const date = new Date(dateTimeStr);
    return date.toISOString();
  }
  
  // If 12-hour format, convert to ISO
  if (dateTimeStr.includes("AM") || dateTimeStr.includes("PM")) {
    const [time, period] = dateTimeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    const isPM = period === 'PM';
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    const date = new Date(`${year}-${month}-${day} ${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
    return date.toISOString();
  }
  
  // If ISO format without Z, add Z
  if (dateTimeStr.includes('T')) {
    const date = new Date(dateTimeStr);
    return date.toISOString();
  }
  
  return dateTimeStr;
};
const CurrentRoute = ({
  setSelectedTab,
  setSelectedView,
  currentRouteName,
  currentRoute,
  onRouteSave,
  clearCurrentRoute,
  setHasRouteChanged,
  setShowUnsavedChangesModal,
  hasRouteChanged,
  showUnsavedChangesModal,
  clickedClear,
  setClickedClear,
  onRouteChange,
  setRouteName,
  isDesktopMap,
  onSelectPractitioner,
}: IMapRouterProps) => {
  const [waypoints, setWaypoints] = useState<(Waypoint | null)[]>([null, null]);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isFirstLocation, setIsFirstLocation] = useState(true);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [selectedWaypoint, setSelectedWaypoint] = useState<any>(waypoints[1]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDragging, setIsDragging] = useState(false);
  const [clickTime, setClickTime] = useState(new Date());
  const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
  const [firstStartTime, setFirstStartTime] = useState(formatTime(new Date()));
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [saveRouteDrawerOpened, { open: openSaveRouteDrawer, close: closeSaveRouteDrawer }] = useDisclosure(false);
  const [routeDate, setRouteDate] = useState<string | null>(dayjs().format('YYYY-MM-DD'));
  const [tempRouteName, setTempRouteName] = useState(currentRoute?.name || currentRouteName);

  React.useEffect(() => {
    if (clickedClear && !showUnsavedChangesModal) {
      setShowUnsavedChangesModal(false);
      setClickedClear(false);
      store.setSelectedPractitioners([]);
      setWaypoints([null, null]);
      setHasRouteChanged(false);
      clearCurrentRoute();
      store.setCurrentDirections([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUnsavedChangesModal, clickedClear]);
  React.useEffect(() => {
    console.log("hasRouteChanged", hasRouteChanged);
  }, [hasRouteChanged]);

  React.useEffect(() => {
    if (currentRoute?.name) {
      setTempRouteName(currentRoute.name);
    }
  }, [currentRoute]);


  const formatDateTimeToAMPM = (dateTimeStr: string) => {
    if (!dateTimeStr) return "";
    
    // Handle both ISO datetime strings (2025-10-17T20:50:00) and space-separated datetime strings
    let time: string;
    if (dateTimeStr.includes('T')) {
      // ISO format: extract time part after 'T'
      time = dateTimeStr.split('T')[1];
    } else {
      // Space-separated format: extract time part after space
      time = dateTimeStr.split(' ')[1];
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    // Return formatted time
    return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
  }

  const handleRoutes = (opt: string) => {
    switch (opt) {
      case 'google':
        OpenMapsNavigation()
        break;
      case 'waze':
        OpenWazeNavigation()
        break;
      case 'apple':
        OpenAppleMapsNavigation()
        break;
    }
    setIsGoRouteModalOpen(false)
  }

  const OpenMapsNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);
    
    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Build Google Maps URL for the current waypoint only
    const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedWaypoint.latitude},${selectedWaypoint.longitude}`;
    // Open in new tab
    window.open(url, '_blank');
  }
  const OpenWazeNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);

    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Navigate to destination directly (iOS-compatible)
    const url = `https://waze.com/ul?ll=${selectedWaypoint.latitude},${selectedWaypoint.longitude}&navigate=yes`;
    window.open(url, '_blank');
  }

  const OpenAppleMapsNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);

    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Navigate to destination directly (iOS-compatible)
    const url = `maps://?daddr=${selectedWaypoint.latitude},${selectedWaypoint.longitude}`;
    console.log('Apple Maps url', url);
    window.open(url, '_blank');
  }

  React.useEffect(() => {
    onRouteChange(waypoints?.filter((w) => w)?.length || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints])

  React.useEffect(() => {
    console.log("first useEffect", currentRoute);
    firstStartTimeUpdate(parseStartTime(store.selectedPractitioners[0]?.start_time));
    if(currentRoute?.practitioner_ids && store.selectedPractitioners.length === 0) {
      const getPractitioners = async () => {
        const response = await postData('/api/get_multiple_practitioners_by_ids', {
          practitioner_ids: currentRoute.practitioner_ids
        });
        const practitioners = response.practitioners || [];
        let firstLocation: any = null;
        let lastLocation: any = null;

        if (currentRoute.locations[0].is_starting_location === "1") {
          firstLocation = {
            address: currentRoute.locations[0].address,
            latitude: currentRoute.locations[0].latitude,
            longitude: currentRoute.locations[0].longitude,
            isFirstLocation: true,
            start_time: currentRoute.locations[0]?.start_time
          }
        } 
        if (currentRoute.locations[currentRoute.locations.length - 1].is_ending_location === "1") {
          lastLocation = {
            address: currentRoute.locations[currentRoute.locations.length - 1].address,
            latitude: currentRoute.locations[currentRoute.locations.length - 1].latitude,
            longitude: currentRoute.locations[currentRoute.locations.length - 1].longitude,
            isLastLocation: true
          }
        }
        // Update both waypoints and store
        const startedWaypoints = [firstLocation, ...(
            practitioners
              .map((p: any, practitionerIndex: number) => {
                const defaultAddress = p.addresses?.find((a: any) => a.id === p.default_address);
                // Calculate the actual index in currentRoute.locations
                // If there's a firstLocation, practitioner 0 maps to location 1
                // const locationIndex = firstLocation ?  : practitionerIndex;
                const routeLocation = currentRoute.locations[practitionerIndex + 1];

                return {
                  ...defaultAddress, // Keep address info for display
                  ...p, // Keep practitioner info
                  // CRITICAL: Use coordinates from the route, not from the practitioner's address
                  latitude: routeLocation.latitude,
                  longitude: routeLocation.longitude,
                  address: routeLocation.address, // Use route's address as source of truth
                }
              })
        ), lastLocation].map((waypoint: any, index: number) => {
          console.log("waypointx",waypoint)
          if(waypoint) {
            waypoint.start_time = currentRoute.locations[index].start_time;
            waypoint.duration = parseInt(currentRoute.locations[index].duration);
            return waypoint;
          }
          return null; // Explicitly return null when waypoint is falsy
        });
        console.log("startedWaypoints",startedWaypoints)
        const loadedWaypoints = startedWaypoints.filter(wp => wp !== null);

        // Saved routes persist their computed legs/ETAs/polyline. Rendering
        // from that cache avoids a billable optimization call on every open;
        // the id check guards against stale caches (stops changed since save),
        // in which case we fall through to the live recompute below.
        const cached = currentRoute?.computed_route;
        if (
          cached?.stops?.length === loadedWaypoints.length &&
          cached.stops.every((s: any, i: number) => (s.id ?? null) === (loadedWaypoints[i]?.id ?? null))
        ) {
          cached.stops.forEach((s: any, i: number) => {
            loadedWaypoints[i].travelTime = s.travelTime ?? undefined;
            loadedWaypoints[i].arriveTime = s.arriveTime || undefined;
            loadedWaypoints[i].waitDuration = s.waitDuration ?? undefined;
          });
          if (cached.polyline) {
            lastRoutePolyline = cached.polyline;
            store.setCurrentDirections(decode(cached.polyline).map(([lat, lon]) => [lon, lat]));
          }
          lastOptimizedWaypointsKey = waypointsFingerprint(currentRoute?.id, loadedWaypoints);
        }

        setWaypoints(loadedWaypoints);

        store.setSelectedPractitioners(loadedWaypoints);
      };
      getPractitioners();
    }else{
     setWaypoints(store.selectedPractitioners);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute]);

  // Add useEffect to update waypoints when selectedPractitioners changes
  React.useEffect(() => {
    // Only run this effect if currentRoute is already loaded and we have selectedPractitioners
    if (store.selectedPractitioners.length > 0 && !currentRoute?.is_loading) {
 
      let firstLocation: any = null;
      let lastLocation: any = null;
      if (store.selectedPractitioners[0]?.isFirstLocation) {
        firstLocation = {
          address: store.selectedPractitioners[0]?.address,
          latitude: store.selectedPractitioners[0]?.latitude,
          longitude: store.selectedPractitioners[0]?.longitude,
          isFirstLocation: true,
          travelTime: store.selectedPractitioners[0].travelTime,
          start_time: store.selectedPractitioners[0]?.start_time
        }
      } 
      if (store.selectedPractitioners[store.selectedPractitioners.length - 1]?.isLastLocation) {
        lastLocation = {
          address: store.selectedPractitioners[store.selectedPractitioners.length - 1]?.address,
          latitude: store.selectedPractitioners[store.selectedPractitioners.length - 1]?.latitude,
          longitude: store.selectedPractitioners[store.selectedPractitioners.length - 1]?.longitude,
          isLastLocation: true,
          arriveTime: store.selectedPractitioners[store.selectedPractitioners.length - 1].arriveTime
        }
      }
      // Update waypoints with current first/last locations and all practitioners
      console.log("firstLocation",firstLocation)
      setFirstStartTime(parseStartTime(firstLocation?.start_time));
      setWaypoints([firstLocation, ...store.selectedPractitioners.filter(p => !p?.isFirstLocation && !p?.isLastLocation), lastLocation]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.selectedPractitioners, currentRoute]);

  // Add useEffect to initialize times when practitioners change
  React.useEffect(() => {
    
    const wp = waypoints.filter(wp => wp !== null);
    const waypointsKey = waypointsFingerprint(currentRoute?.id, waypoints);

    if (wp.length >= 2 && !isOptimizing && lastOptimizedWaypointsKey !== waypointsKey) {
      console.log("optimizing route");
      setIsOptimizing(true);
      lastOptimizedWaypointsKey = waypointsKey;
      onOptimizeRoute(false);
    } else if (wp.length < 2) {
      console.log("not optimizing route");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waypoints, isOptimizing]); 

  const handleSearchClick = (isFirst:boolean) => {
    setIsFirstLocation(isFirst);
    setIsSearchModalOpen(true);
  }

  const handleDragStart = () => {
    setIsDragging(true);
    setClickTime(new Date());
  };


  const renderSearchModal = () =>{
    return(
      <SearchModal
      isOpen={isSearchModalOpen}
      onClose={handleModalClose}
      searchTerm={searchTerm}
      suggestions={suggestions}
      onSuggestionClick={handleFirstLastLocation}
      firstLocation={isFirstLocation}
      onSearchChange={handleSearchChange}
    />
    )
  }
  const checkDuration = () => {
    if(!currentRoute){
      const currentDuration = waypoints.find((waypoint: any) => waypoint?.id === selectedWaypoint?.id)?.duration;
      if (currentDuration) {
        if (parseInt(currentDuration) < 60) {
          return `${currentDuration} mins`;
        } else {
          const hours = parseInt(currentDuration) / 60;
          return `${hours} hours`;
        }
      }
      return '15 mins';
    }
    const currentDuration = store.selectedPractitioners?.find((practitioner: any) => practitioner.id === selectedWaypoint.id)?.duration;
    if (!currentDuration) return '15 mins';
    if (currentDuration < 60) {
      return `${currentDuration} mins`;
    } else {
      const hours = currentDuration / 60;
      return `${hours} hours`;
    }
  }

  const checkStartTime = () => {
    if(!currentRoute){
      const currentStartTime = selectedWaypoint?.start_time;
      if (currentStartTime) {
        return formatDateTimeToAMPM(currentStartTime);
      }
      return '';
    }
    const currentStartTime = store.selectedPractitioners?.find((practitioner: any) => practitioner.id === selectedWaypoint.id)?.start_time;

    return currentStartTime?formatDateTimeToAMPM(currentStartTime):'';
  }

  const parseLength = (length: number) => {
    if (!length) return '15m';
    if (length < 60) {
      return `${length} m`;
    } else {
      const hours = length / 60;
      return `${hours} h`;
    }
  }
  const renderMeetingModal = () => {
    if (!selectedWaypoint) return null;
    return (
      <MeetingModal
        isOpen={isMeetingModalOpen}
        isDesktopMap={isDesktopMap}
        practitioner={selectedWaypoint}
        onClose={() => {
          setIsMeetingModalOpen(false);
          setSelectedWaypoint(null);
        }}
        updatePractitioner={updatePractitioner}
        onDelete={() => {
          handlePractitionerRemoval(selectedWaypoint);
          setIsMeetingModalOpen(false);
          setSelectedWaypoint(null);
        }}
        setSelectedView={setSelectedView}
        checkDuration={checkDuration}
        checkStartTime={checkStartTime}
        setGoRouteModalIsOpen={setIsGoRouteModalOpen}
        onSelectPractitioner={onSelectPractitioner}
      />
    );
  }

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    const response = await getPlacesAutocomplete(value) as PlacesResponse;
    if (response.success) {
      setSuggestions(response.places);
    }
  }

    // get latitude and longitude from location
    const getPlaceDetails = async (placeId: string) => {
      try {
        const result = await postData('/api/google_places_details', {
          place_id: placeId
        });
        if (result.success) {
          return {
            latitude: result.place.geometry.location.lat,
            longitude: result.place.geometry.location.lng
          };
        }
        throw new Error(result.error_msg || 'Failed to get place details');
      } catch (error) {
        console.error('Error getting place details:', error);
        throw error;
      }
    };
 
  const handleFirstLastLocation = async (place: any, isFirstLocation: boolean, currentLocation: boolean) => {
    let location: any;
    try {
      if(currentLocation) {
        location = {
          address: "Current Location",
          latitude: place.geometry.location.lat,
          longitude: place.geometry.location.lng
        };
      } else {
        const { latitude, longitude } = await getPlaceDetails(place.place_id);
        location = {
          address: place.description,
          latitude,
          longitude
        };
      }
      if(isFirstLocation) {
        setWaypoints(prev => {
          let newWaypoints;
          const currentTime = formatTime(new Date());
          const startTimeFormatted = parseStartTime(currentTime);
          if (prev[0] === null) {
            newWaypoints = [{...location, isFirstLocation: true, start_time: startTimeFormatted}, ...prev.slice(1)];
          } else {
            newWaypoints = [{...location, isFirstLocation: true, start_time: startTimeFormatted}, ...prev];
          }
          store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
          setHasRouteChanged(true);
          setFirstStartTime(currentTime);
          return newWaypoints;
        });
      } else {
        setWaypoints(prev => {
          let newWaypoints;
          if (prev[prev.length - 1] === null) {
            newWaypoints = [...prev.slice(0, -1), {...location, isLastLocation: true}];
          } else {
            newWaypoints = [...prev, {...location, isLastLocation: true}];
          }
          store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
          setHasRouteChanged(true);
          console.log("newWaypoints after last location", newWaypoints);
          return newWaypoints;
        });
      }
      setSearchTerm('');
      setSuggestions([]);
      setIsSearchModalOpen(false);
    } catch (error) {
      console.error('Error setting location:', error);
    }
  }

  const handlePractitionerRemoval = (practitioner: any) => {
    store.removeFromRoute(practitioner);
    setWaypoints(prev => {
      const newWaypoints = prev.filter(wp => wp?.id !== practitioner?.id);
      store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
      setHasRouteChanged(true);
      return newWaypoints;
    });
  };

  const handleModalClose = () => {
    setIsSearchModalOpen(false);
    setSuggestions([]);
  }
  const updatePractitioner = async (practitionerId: number, updateData: any) => {
    // Update the waypoint in the local state while preserving order
    const updatedWaypoints = waypoints.map(wp => {
      if (wp === null) return wp;
      return wp.id === practitionerId 
        ? { ...wp, duration: updateData.meeting_duration, start_time: updateData.start_time ? parseStartTime(updateData.start_time) : ""}
        : wp;
    });
    store.setSelectedPractitioners(updatedWaypoints.filter(wp => wp !== null));
    setWaypoints(updatedWaypoints);
    if (updateData.hasChanges) {
      setTimeout(() => {
        onOptimizeRouteTours(true, updatedWaypoints);
      }, 0);
    }
    
    // Update store with the same order as waypoints
  };

  const displayTime = (waypoint: any) => {
    if(waypoint.arriveTime && waypoint.arriveTime !== "") {
      return waypoint.arriveTime;
    }
    else if(waypoint.start_time && waypoint.start_time !== "") {
      return parseStartTime(waypoint.start_time);
    }
    return formatTime(new Date());
  }
  const SortableItem = ({ id, waypoint, arriveTime, index, onClick }: { id: number, waypoint: any, arriveTime: string, index: number, onClick: () => void }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    const notNullWaypoint = waypoints.filter(wp => wp !== null);
    return (
      <div
        ref={setNodeRef} 
        style={style}
        onClick={() => {
          setSelectedWaypoint(waypoint);
          setIsMeetingModalOpen(true);
        }}
      >
        <div key={waypoint?.id}>
          <div className={styles.waypointSubWrapper}>
            <div
              key={waypoint?.id} className={styles.waypointWapper}>
              <DraggableIcon 
                {...attributes}
                {...listeners}
                style={{
                  cursor: "grab",
                  touchAction: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  WebkitTouchCallout: "none",
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation(); 
                }}
              />
              <div className={styles.waypointInfo}>
                <p className={styles.headerText}>{index + (waypoints[0]? 2 : 1)}. {waypoint?.name || waypoint?.primary_name || ""}</p>
                <div className={styles.travelTextContainer}>
                  <div className={styles.timeContainer}>
                    <p className={styles.timeText}>{displayTime(waypoint)}</p>
                    {waypoint.start_time && <span className={styles.startTimeBadge}>Has Start Time</span>}
                  </div>
                  <div className={styles.travelLengthContainer}>
                    Length:
                    <p className={styles.travelLengthText}>{parseLength(waypoint?.duration)}</p>
                  </div>
                </div>
              </div>
              <div className={styles.expandIconContainer}>
                <ExpandIcon width={16} height={16} />
              </div>
            </div>
            {!isNaN(waypoint?.travelTime) && waypoint.id !== notNullWaypoint[notNullWaypoint.length - 1]?.id  && (
              <div className={styles.custonSplitter}>
                <div className={styles.travelTime}>
                  <p className={styles.travelTimeText}>
                    Travel Time: {waypoint.travelTime} min.
                    {(() => {
                      const nextWaypoint = notNullWaypoint[index + 2];
                      return nextWaypoint?.waitDuration ? <span className={styles.earlyArrivalText}>{`  Arriving ${nextWaypoint.waitDuration} min early`}</span> : null;
                    })()}
                  </p>
                </div>
              </div>
            )}
          </div>

          </div>
      </div>
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if(clickTime && new Date().getTime() - clickTime.getTime() < 100) {
      setSelectedWaypoint(waypoints[active?.id]);
      setIsMeetingModalOpen(true);
    }
    if (active?.id !== over?.id) {
      
      const oldIndex = active?.id;
      const newIndex = over?.id;
      setHasRouteChanged(true);
      const reorderedWaypoints = arrayMove(waypoints, oldIndex, newIndex);
      const clearedWaypoints = reorderedWaypoints.map(wp => {
        if (wp) {
          return { ...wp, waitDuration: undefined, travelTime: undefined };
        }
        return wp;
      });
      store.setSelectedPractitioners(clearedWaypoints.filter(wp => wp !== null));
      console.log("reorderedWaypoints", clearedWaypoints);
    }
    setTimeout(() => setIsDragging(false), 0);
  };

  const onClearLocations = (options?: { bypassModal?: boolean }) => {
    const shouldBypassModal = options?.bypassModal ?? false;

    if (hasRouteChanged && !shouldBypassModal) {
      setShowUnsavedChangesModal(true);
      setClickedClear(true);
    } else {
      store.setSelectedPractitioners([]);
      setWaypoints([null, null]);
      setHasRouteChanged(false);
      clearCurrentRoute();
      setClickedClear(false);
      store.setCurrentDirections([])
    }
  };

  const onSaveRoute = async (routeId?: number) => {
    setRouteName(tempRouteName);
    let newRouteId;
    if(routeId) {
      await updateRoute();
    } else {
      newRouteId = await saveRoute();
    }

    if (currentRoute || newRouteId) {
      onRouteSave(newRouteId ||currentRoute?.id );
      setHasRouteChanged(false); 
      onClearLocations({ bypassModal: true }); // Call with option to bypass modal
      setSelectedTab(Tab.SAVED_ROUTES);
    } 
  }

  const handleFirstLocationStartTime = () => {
    setSelectedWaypoint(waypoints[0]);
    setIsStartTimeModalOpen(true);
  }

  // Snapshot of the last computed legs/ETAs/polyline, persisted with the
  // route so reopening it can render without a new optimization call.
  const buildComputedRoute = () => ({
    polyline: lastRoutePolyline || "",
    stops: waypoints.filter(wp => wp !== null).map((wp: any) => ({
      id: wp?.id ?? null,
      travelTime: wp?.travelTime ?? null,
      arriveTime: wp?.arriveTime || "",
      waitDuration: wp?.waitDuration ?? null,
    })),
  });

  const saveRoute = async () => {
    const address = waypoints.map((waypoint: any) => waypoint?.address || "");
    const latitude = waypoints.map((waypoint: any) => waypoint?.latitude || "");
    const longitude = waypoints.map((waypoint: any) => waypoint?.longitude || "");
    const start_time = waypoints.map((waypoint: any, index: number) => {
      // If it's the first location and has no start_time but firstStartTime is set, use that
      if (index === 0 && waypoint?.isFirstLocation && !waypoint?.start_time && firstStartTime) {
        return parseStartTime(firstStartTime);
      }
      return waypoint?.start_time || "";
    });
    const end_time = waypoints.map((waypoint: any) => waypoint?.end_time || "");
    const duration = waypoints.map((waypoint: any) => waypoint?.duration);
    const routeData = {
      "name": tempRouteName || "",
      "fmc_email": store.impersonatedUser,
      "first_location": waypoints[0]?.isFirstLocation || false,
      "last_location": waypoints[waypoints.length - 1]?.isLastLocation || false,
      "address": address,
      "latitude": latitude,
      "longitude": longitude,
      "start_time": start_time,
      "end_time": end_time,
      "duration": duration,
      "date": routeDate,
      "computed_route": buildComputedRoute(),
    }

    if (waypoints && waypoints.length > 0) {
      const { route_id } =await postData('/api/add_route', {
        route_data: routeData,
        practitioner_ids: waypoints.filter(wp => !wp?.isFirstLocation && !wp?.isLastLocation).map((waypoint: any) => waypoint?.id)
      });
      return route_id;
    }
    return 0;
  }

  const updateRoute = async () => {
    const address = waypoints.map((waypoint: any) => waypoint?.address || "");
    const latitude = waypoints.map((waypoint: any) => waypoint?.latitude || "");
    const longitude = waypoints.map((waypoint: any) => waypoint?.longitude || "");
    const start_time = waypoints.map((waypoint: any, index: number) => {
      // If it's the first location and has no start_time but firstStartTime is set, use that
      if (index === 0 && waypoint?.isFirstLocation && !waypoint?.start_time && firstStartTime) {
        return parseStartTime(firstStartTime);
      }
      return waypoint?.start_time || "";
    });
    const end_time = waypoints.map((waypoint: any) => waypoint?.end_time || "");
    const duration = waypoints.map((waypoint: any) => waypoint?.duration);
    const routeData = {
      "name": tempRouteName || "",
      "fmc_email": store.impersonatedUser,
      "first_location": waypoints[0]?.isFirstLocation || false,
      "last_location": waypoints[waypoints.length - 1]?.isLastLocation || false,
      "address": address,
      "latitude": latitude,
      "longitude": longitude,
      "start_time": start_time,
      "end_time": end_time,
      "duration": duration,
      "date": routeDate,
      "computed_route": buildComputedRoute(),
    }
    await postData('/api/update_route', {
      route_id: currentRoute?.id,
      route_data: routeData,
      practitioner_ids: waypoints.filter(wp => !wp?.isFirstLocation && !wp?.isLastLocation).map((waypoint: any) => waypoint?.id)
    });
  }
  const onOptimizeRoute = async (waypointOrder?: boolean) => {
    
    await onOptimizeRouteTours(waypointOrder);
    return
    
  }


  // ——— add at top if you don't already have it —————————————
  interface LatLng {
    lat: number;
    lng: number;
    name?: string;
}

interface RouteResponse {
  routes: Array<{
    beforeDepartureIndex?: Array<number>;
    skippedIntermediateWaypointIndex?: Array<number>;
    visits?: Array<{ startTime: string, duration: string }>;
    transitions?: Array<{ travelDuration: string }>;
    optimizedIntermediateWaypointIndex?: number[];
    legs: Array<{ duration: string, startTime: string, waitDuration: string }>;
    routePolyline?: { points: string };
    summary: {
      hasTrafficInfeasibilities: boolean;
      totalDuration: string;
      travelDuration: string;
      visitDuration: string;
      totalDistanceMeters: number;
      performedShipmentCount: number;
    };
  }>;
}

async function onOptimizeRouteTours(waypointOrder?: boolean, updatedWaypoints?: Array<Waypoint | null>): Promise<void> {
  // Use the passed waypoints if available, otherwise use state
  const currentWaypoints = updatedWaypoints || waypoints;
  console.log("currentWaypoints in onOptimizeRouteTours", currentWaypoints);
  // 1) UI spin
  setIsOptimizing(true);
  
  // 2) Start & end LatLng
  let startLatLng = null;
  if(currentWaypoints[0] !== null){
    startLatLng = { lat: +currentWaypoints[0]!.latitude!, lng: +currentWaypoints[0]!.longitude! };
  }
      
  let endLatLng = null;
  if(currentWaypoints[currentWaypoints.length - 1] !== null){
    endLatLng = { lat: +currentWaypoints[currentWaypoints.length - 1]!.latitude!, lng: +currentWaypoints[currentWaypoints.length - 1]!.longitude! };
  }
  // 3) Middle waypoints for the API
  const waypointData: LatLng[] = currentWaypoints
    .filter(wp => wp !== null)
    .filter(wp=> !wp!.isFirstLocation && !wp!.isLastLocation)
    .map((wp) => {
      let startTime = "";
      if (wp!.start_time) {
        // Extract time from stored start_time and apply to today's date
        const now = new Date();
        const storedTime = new Date(convertToApiFormat(wp!.start_time));
        const updatedTime = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          storedTime.getHours(),
          storedTime.getMinutes(),
          storedTime.getSeconds()
        );
        startTime = updatedTime.toISOString();
      }
      return {
        name: wp!.name,
        lat: +wp!.latitude!,
        lng: +wp!.longitude!,
        startTime: startTime,
        duration: wp!.duration
      };
    })
  console.log('waypointData', waypointData)
  // 4) Compute initialLocalTime (same logic as onOptimizeRoute)
  let initialLocalTime: Date;
  const firstLocation = currentWaypoints.find(wp => wp?.isFirstLocation);
  if (firstLocation && firstLocation?.start_time) {
    // Grab only the hour from firstLocation.start_time (already in ISO), set it to today's date
    {
      const now = new Date();
      const firstLocationTime = new Date(convertToApiFormat(firstLocation?.start_time || ""));
      initialLocalTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        firstLocationTime.getHours(),
        firstLocationTime.getMinutes(),
        firstLocationTime.getSeconds() // use seconds as well for full ISO parity
      );
    }
    console.log('firstLocation', firstLocation)
    console.log('initialLocalTime 1', convertToApiFormat(firstLocation?.start_time || ""))
  }
  else if (currentWaypoints[0] !== null && firstStartTime) {
    initialLocalTime = new Date(parseStartTime(firstStartTime));
    console.log('initialLocalTime 2', initialLocalTime)
  } else if (currentWaypoints[1]?.start_time) {
    initialLocalTime = new Date(currentWaypoints[1]!.start_time!);
    console.log('initialLocalTime 3', initialLocalTime)
  } else {
    initialLocalTime = new Date();
    console.log('initialLocalTime 4', initialLocalTime)
  }
  if(currentWaypoints.filter(wp=> wp?.isFirstLocation).length === 0){
    // Find the waypoint with the earliest start_time among non-first/last location waypoints
    const waypointsWithStartTime = currentWaypoints
      .filter(wp => wp !== null && !wp.isFirstLocation && !wp.isLastLocation && wp.start_time)
      .sort((a, b) => a!.start_time!.localeCompare(b!.start_time!));
    if (waypointsWithStartTime.length > 0) {
      initialLocalTime = new Date(new Date(waypointsWithStartTime[0]!.start_time!).getTime() - 1);
    }else{
      initialLocalTime = new Date(initialLocalTime.getTime() - 60 * 1000);
    }
  }
  
  if (currentRoute?.route_date) {
    const now = new Date();
    // Keep the time from initialLocalTime but ensure we use today's date
    initialLocalTime = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      initialLocalTime.getHours(),
      initialLocalTime.getMinutes(),
      initialLocalTime.getSeconds()
    );
  }
 
  const departureTimeUtc = initialLocalTime.toISOString();
  try {
    // 5) Hit the Tours endpoint

    
    const endTime = new Date(new Date(departureTimeUtc).getTime() + Math.max(124, waypoints.length *0.8) * 60 * 60 * 1000).toISOString();
    const res = await postData(
      '/api/google_routes_optimization_tours',
      {
        origin: startLatLng,
        destination: endLatLng,
        waypoints: waypointData,
        departureTime: departureTimeUtc,
        optimizeWaypointOrder: waypointOrder,
        endTime: endTime,
      }
    ) as {
      beforeDepartureIndex: any; data: RouteResponse 
};

    const route = res.data.routes[0];
    if(route.beforeDepartureIndex?.length! > 0) {
      const practitionerNames = route.beforeDepartureIndex?.map((index) => waypointData[index].name).join(', ')
      setToastMessage(`Time on ${practitionerNames} is set previous to departure time.`);
      setIsOptimizing(false);
      setShowToast(true);
      return
    }

    // Decode polyline and set route coordinates for drawing
    const _currentDirections: number[][] = [];
    
    // Check for the new routePolyline structure first
    if (route?.routePolyline?.points) {
      const encodedPolyline = route.routePolyline!.points;
      lastRoutePolyline = encodedPolyline;
      const decodedPolyline = decode(encodedPolyline);
      _currentDirections.push(
        ...(decodedPolyline.map(([lat, lon]) => {
          return [lon, lat]
        }))
      );
    }

    
    store.setCurrentDirections(_currentDirections);
    if(route.skippedIntermediateWaypointIndex?.length! > 0 || route.summary.hasTrafficInfeasibilities) {
      console.log('skippedIntermediateWaypointIndex', route.skippedIntermediateWaypointIndex)
      setShowToast(true);
      setToastMessage(`Due to traffic conditions, the following locations with required start times are not possible: ${route.skippedIntermediateWaypointIndex?.map((index) => waypointData[index].name).join(', ')}.  Please remove the start time requirements in order to optimize your route.`);
      setIsOptimizing(false);
      return
    }
    // ─── 6) Normalize into order + legs ─────────────────────────────
    let order: number[];
    let visits: Array<{startTime: string, duration: string }>;
    let legs: Array<{ duration: string, startTime: string, waitDuration: string }> | undefined;

    if (Array.isArray(route.optimizedIntermediateWaypointIndex) && Array.isArray(route.visits)) {
      // ComputeRoutes‐style (fallback)
      order = route.optimizedIntermediateWaypointIndex!;
      visits  = route.visits!.map((l) => ({ startTime: l.startTime, duration: l.duration }));
      legs = route.legs.map((t) => ({ duration: t.duration, startTime: t.startTime, waitDuration: t.waitDuration }));
    } else {
      throw new Error('Unexpected route shape');
    }
    // ────────────────────────────────────────────────────────────────

    // 7) Rebuild optimized waypoints array
    const firstWaypoint = currentWaypoints.filter(wp => wp?.isFirstLocation);
    const lastWaypoint = currentWaypoints.filter(wp=> wp?.isLastLocation);
    const middle = currentWaypoints.filter(wp=> wp !== null).filter(wp=> !wp!.isFirstLocation && !wp!.isLastLocation);
    
    // Create deep copies to preserve original start_time values
    const optimizedMiddle = order.map((idx: number) => {
      const originalWaypoint = middle[idx]!;
      return {
        ...originalWaypoint,
        waitDuration: undefined,
        start_time: originalWaypoint.start_time
      };
    });
    
    let optimizedWaypoints: Array<Waypoint | null> = [
      ...firstWaypoint.map(wp => ({ ...wp, start_time: wp?.start_time })), // Preserve first location start_time
      ...optimizedMiddle,
      ...lastWaypoint
    ];
    optimizedWaypoints = optimizedWaypoints.filter(wp => wp !== null);
    // 8) Compute travelTime & arriveTime


    // subsequent legs → travel + service
    const hasFirstWaypoint = firstWaypoint.length > 0;
    const visitPadding = hasFirstWaypoint ? 1 : 0;
    const legsPadding = hasFirstWaypoint ? 0 : 1;

    for (let i = 0; i < visits.length; i++) {
      const legIndex = i + legsPadding;
      const originIndex = i;
      const destinationIndex = i + 1;
      
      const travelMin = Math.round(parseInt(legs![legIndex]!.duration, 10) / 60);
      optimizedWaypoints[originIndex]!.travelTime = travelMin;
      
      const wait = parseInt(legs![legIndex]!.waitDuration);
      if(wait && wait > 60 && destinationIndex < legs!.length){
        optimizedWaypoints[destinationIndex]!.waitDuration = Math.round(wait / 60);
      }
      const arrival = new Date(visits[i]!.startTime);
      arrival.setMinutes(arrival.getMinutes() + 1);
      optimizedWaypoints[i + visitPadding]!.arriveTime = formatTime(arrival);
    }
    if(lastWaypoint.length > 0){ // if dedicated ending point, manually calculate for it
      // Use the last leg's start time + duration to get arrival time at final destination
      let lastLegStartTime = new Date(legs![legs!.length - 1]!.startTime);
      let lastLegDurationMinutes = Math.round(parseInt(legs![legs!.length - 1]!.duration, 10) / 60);
      console.log("lastLegDurationMinutes for last waypoint", lastLegDurationMinutes);
      lastLegStartTime.setMinutes(lastLegStartTime.getMinutes() + lastLegDurationMinutes + 1);
      optimizedWaypoints[optimizedWaypoints.length - 1]!.arriveTime = formatTime(lastLegStartTime);
      // Set travel time for the last location from the last leg
      optimizedWaypoints[optimizedWaypoints.length - 2]!.travelTime = lastLegDurationMinutes;
    }
    // 9) Commit to state

    lastOptimizedWaypointsKey = waypointsFingerprint(currentRoute?.id, optimizedWaypoints);

    setWaypoints(optimizedWaypoints);
    const finalWps = optimizedWaypoints.filter(
      (wp): wp is Waypoint => wp !== null
    );
    store.setSelectedPractitioners(finalWps);
    setHasRouteChanged(true);

  } catch (err) {
    console.error('Error optimizing via Tours:', err);
  }
  setIsOptimizing(false);
}

  const getPlacesAutocomplete = async (input: string) => {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const currentLocationString = `${lat},${lng}`;
          
          try {
            const result = await postData('/api/google_places_autocomplete', {
              query: input,
              location: currentLocationString,
            });
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, (error) => {
          reject(error);
        });
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  const firstStartTimeUpdate = (startTime: string) => {
      const firstWaypoint = waypoints.filter(wp => wp !== null)[0];
      console.log("firstWaypoint",firstWaypoint)
      setFirstStartTime(startTime);
      if (firstWaypoint) {
        firstWaypoint.start_time = startTime ? parseStartTime(startTime) : (firstStartTime ? firstStartTime : "");
        setWaypoints([firstWaypoint, ...waypoints.slice(1)]);
        store.setSelectedPractitioners([firstWaypoint, ...store.selectedPractitioners.slice(1)]);
     
      }
  }
  return (
    <div className={styles.currentRouteContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.btnsContainer}>
          {/*<Button type="outline" onClick={() => onOptimizeRoute(true)}>OPTIMIZE</Button>*/}
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                onOptimizeRouteTours(true);
                setIsOptimizing(true);
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" >OPTIMIZE</Button>
          </div>
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                setSelectedTab(Tab.LIST_CUSTOMERS)
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" >ADD</Button>
          </div>
        </div>
        <div className={styles.btnsContainer}>
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                onClearLocations()
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" color="#E64B3780" textColor="#E64B37">NEW ROUTE</Button>
          </div>
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                openSaveRouteDrawer();
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" color="#4F7D9C" textColor="white">SAVE</Button>
          </div>
        </div>
      </div>

      {renderSearchModal()}
      {renderMeetingModal()}
      <StartTimeModal 
        isOpen={isStartTimeModalOpen} 
        onClose={() => setIsStartTimeModalOpen(false)} 
        startTime={firstStartTime} 
        setStartTime={firstStartTimeUpdate} 
        setIsStartTimeModalOpen={setIsStartTimeModalOpen}
      />
      <div className={styles.searchContainer}>
        {!waypoints[0] ? (waypoints.length > 2 ? (
          
          <div className={styles.searchInput} onClick={()=>handleSearchClick(true)}>
            <HouseIcon width={'25px'} height={'25px'} style={{marginRight: '15px',}}/>
            <span className={styles.placeholder}>Starting location</span>
          </div>
        ): null) : (
          <div className={styles.firstLocationContainer}>
            <div className={styles.locationContainer}>
              <div className={styles.locationNumber}>1.</div>
              <span className={styles.locationAddress}>{waypoints[0].address}</span>              
            </div>
            <div style={{display:'flex'}}>
              <CloseIcon style={{marginRight: '20px'}} width={30} height={30} onClick={() => {
                setFirstStartTime("");
                setWaypoints(prev => [null, ...prev.slice(1)]);
                setHasRouteChanged(true);
              }}/>
              <div onClick={() => handleFirstLocationStartTime()} className={styles.timeText}>
                {firstStartTime ? (firstStartTime) : formatTime(new Date())}
              </div>
            </div>
            {waypoints[0]?.travelTime ? (
              <div className={`${styles.custonSplitter} ${styles.firstLocationSplitter}`}>
                <div className={styles.travelTime}>
                  <p className={styles.travelTimeText}>Travel Time: {waypoints[0]?.travelTime} minutes</p>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={waypoints.slice(1, -1).map((_, index) => index+1)} strategy={verticalListSortingStrategy}>
            {waypoints.slice(1, -1).map((waypoint, index) => (
              <SortableItem 
                key={index+1} 
                id={index+1} 
                waypoint={waypoint} 
                arriveTime={waypoint?.arriveTime || ""}
                index={index}
                onClick={() => {}}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      {!waypoints[waypoints.length - 1] ? (waypoints.length > 2 ? (
          <div className={styles.searchInput} onClick={()=>handleSearchClick(false)}>
            <FlagIcon width={'20px'} height={'20px'} style={{paddingLeft: '4px', marginRight: '15px'}}/>
            <span className={styles.placeholder}>Ending location</span>
          </div>
        ) : null) : (
          <div className={styles.firstLocationContainer}>
            <div className={styles.locationContainer}>
              <div className={styles.locationNumber}>{store.selectedPractitioners.length}.</div>
              <span className={styles.locationAddress}>{waypoints[waypoints?.length - 1]?.address}</span>              
            </div>
            <div style={{display:'flex'}}>
              <CloseIcon style={{marginRight: '20px'}} width={30} height={30} onClick={() => {
                setWaypoints(prev => [...prev.slice(0, -1), null]);
                setHasRouteChanged(true);
              }}/>
              <div className={styles.timeText}>
                {waypoints[waypoints.length - 1]?.arriveTime}
              </div>
            </div>
          </div>
        )}
      
      <RoutesTab isOpen={isGoRouteModalOpen} setIsOpen={setIsGoRouteModalOpen} onSelect={handleRoutes} />
      <Toastr closeTime={5000} backgroundColor={'#E64B37'} message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} />

      {/* Calendar Export - Only show for earlyRelease users */}
      {store.user?.earlyRelease && (
        <RouteCalendarExport routeDate={routeDate} meetings={waypoints} />
      )}

      <Drawer
        opened={saveRouteDrawerOpened}
        onClose={closeSaveRouteDrawer}
        position="bottom"
        size="610px"
        radius="30px 30px 0 0"
        zIndex={5000}
        title={
          <Title 
            order={2} 
            fw={700}
            size={24}
            c="#4F7D9C"
            ff="Manrope, sans-serif"
          >
            Save Route
          </Title>
        }
        styles={{
          header: {
            borderBottom: "1px solid #E0E0E0",
            paddingBottom: "0px",
            paddingLeft: "0px",
            paddingRight: "0px",
            marginLeft: "30px",
            marginRight: "30px",
          }
        }}
      >
        <Box
          pl={25}
          pr={25}
          pt={10}
          pos="relative"
        >
           <Stack gap={'xs'}>
             <div className={styles.modalLabel}>Name</div> 
             <div className={styles.modalRouteNameHeader}>
               <input
                 type="text"
                 value={tempRouteName}
                 onChange={(e) => setTempRouteName(e.target.value)}
                 className={styles.modalRouteNameInput}
                 placeholder={"Enter a new Route Name"}
               />
                 {tempRouteName && (
                   <CloseIcon onClick={() => setTempRouteName('')} />
                 )}
             </div>

             <div className={styles.modalLabel}>Date</div>
             <DatePickerInput
               value={routeDate}
               onChange={setRouteDate}
               placeholder="Select date"
               valueFormat="MMMM, D, YYYY"
               firstDayOfWeek={0}
               popoverProps={{
                zIndex: 6000,
                styles: {
                  dropdown: {
                    border: '2px solid #E0E0E0'
                  }
                }
                
               }}
               styles={{
                 input: {
                   fontSize: '16px',
                   paddingBottom: '5px',
                   border: 'none',
                   borderBottom: '1px solid #E0E0E0',
                   backgroundColor: 'transparent',
                   borderRadius: '0',
                   padding: '0px',
                   '&:focus': {
                     borderBottom: '1px solid #4F7D9C',
                     boxShadow: 'none'
                   },
                   button: {
                    padding: '0px'
                   }

                 }
               }}
             />
           </Stack>
           
          
        </Box>

        <div className={styles.saveRouteButtonContainer}>
            <button 
              className={styles.saveRouteButtonEnabled + " " + styles.renameRouteButton}
              disabled={currentRoute?.id ? false : true}
              onClick={() => {
                onSaveRoute(currentRoute?.id);
                closeSaveRouteDrawer();
              }}
            >
              Save as<br/>Existing Route
            </button>

            <button 
              className={styles.saveRouteButtonEnabled + " " + styles.saveAsNewRouteButton}
              disabled={tempRouteName.length > 0 ? false : true}
              onClick={() => {
                // Handle save as new route
                onSaveRoute();
                closeSaveRouteDrawer();
              }}
            >
              Save as<br/>New Route
            </button>
          </div>
      </Drawer>
    </div>
  );
};

export default observer(CurrentRoute);